import type { ReactNode } from 'react'
import { LogOut } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

import { Avatar } from './primitives'

export interface NavItem {
  to: string
  label: string
  icon: ReactNode
  /** A count beside the label — 47 businesses, 2 open tickets. */
  count?: number
  /** A quiet dot, alongside a word elsewhere on the destination screen. */
  attention?: boolean
  end?: boolean
}

export interface NavSection {
  id: string
  title: string
  items: NavItem[]
}

/**
 * Eleven destinations do not fit in five tab slots, which is the whole reason
 * these consoles are a sidebar and the phone app is a tab bar.
 *
 * A destination a role cannot use is ABSENT from `sections`, never rendered
 * disabled: a greyed-out item still tells a support account what exists and
 * invites them to ask for it.
 */
export function SidebarNav({
  brand,
  brandSub,
  sections,
  user,
  onSignOut,
  className,
}: {
  brand: string
  brandSub: string
  sections: NavSection[]
  user: { name: string; role: string }
  /** Omitted, the block is just an identity. Given, it can be ended. */
  onSignOut?: () => void
  className?: string
}) {
  return (
    <aside
      className={cn(
        'flex w-[238px] shrink-0 flex-col overflow-y-auto border-e border-glass-border bg-glass px-3 py-4',
        'backdrop-blur-glass',
        className,
      )}
    >
      <div className="flex items-center gap-2.5 px-2 pt-1 pb-1.5">
        <span
          className="flex size-[34px] shrink-0 items-center justify-center rounded-[11px] bg-linear-to-br from-accent-glow to-accent text-white shadow-[0_4px_12px_var(--color-accent-shadow)]"
          aria-hidden="true"
        >
          <BullMark />
        </span>
        <span className="font-display text-[15px] leading-tight font-extrabold tracking-[-0.3px] text-dark">
          {brand}
          <span className="block text-[11px] font-medium tracking-normal text-muted">
            {brandSub}
          </span>
        </span>
      </div>

      <nav className="flex flex-col">
        {sections.map((section) => (
          <div key={section.id}>
            <h2 className="px-2.5 pt-[18px] pb-[7px] font-display text-[10px] font-bold tracking-[0.11em] text-muted uppercase">
              {section.title}
            </h2>
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-[10px] px-2.5 py-2.5 text-[13.5px] font-semibold',
                    isActive
                      ? 'bg-accent-glass text-accent-dark'
                      : 'text-sub hover:bg-glass hover:text-dark',
                  )
                }
              >
                {({ isActive }: { isActive: boolean }) => (
                  <>
                    <span
                      className={cn(
                        'flex size-[17px] shrink-0 items-center justify-center',
                        isActive ? 'text-accent-dark' : 'text-muted',
                      )}
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    <span className="truncate">{item.label}</span>
                    {item.count !== undefined ? (
                      <span className="ms-auto font-display text-[11px] font-bold tnum text-muted">
                        {item.count}
                      </span>
                    ) : null}
                    {item.attention && item.count === undefined ? (
                      <span className="ms-auto size-[7px] rounded-full bg-warn" aria-hidden="true" />
                    ) : null}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="mt-auto flex items-center gap-2.5 border-t border-muted/15 pt-3.5">
        <Avatar name={user.name} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[13px] font-bold text-dark">{user.name}</div>
          <div className="truncate text-[10.5px] text-muted">{user.role}</div>
        </div>
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            aria-label={t('auth.signOut')}
            title={t('auth.signOut')}
            className="flex size-8 shrink-0 items-center justify-center rounded-[10px] text-muted hover:text-dark"
          >
            <LogOut className="size-4" />
          </button>
        ) : null}
      </div>
    </aside>
  )
}

/** The product mark. Defined once, drawn in currentColor. */
function BullMark() {
  return (
    <svg viewBox="0 0 64 64" className="size-[21px]" fill="none" aria-hidden="true">
      <path
        d="M18.5 25.5C11.5 25.5 5.5 21.5 4.5 14.5c6-2 12 1 15 7M45.5 25.5c7 0 13-4 14-11-6-2-12 1-15 7"
        stroke="currentColor"
        strokeWidth="4.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 15c8.8 0 15.5 4.6 15.5 12.4v6.2C47.5 44 40.8 50.5 32 50.5S16.5 44 16.5 33.6v-6.2C16.5 19.6 23.2 15 32 15Z"
        fill="currentColor"
      />
    </svg>
  )
}
